import {
  resolveAreaFormula,
  formulaDimensions,
  computeMarineArea,
  lineTotal,
  estimateFreeboardIn,
} from './marineCalc.js';
import { marineAttrsOf, getMarineRate } from './marineService.js';

/**
 * Price one area-priced line from the catalog row and the craft, with no UI.
 *
 * This logic used to live inside MarineCalculatorModal, which meant the
 * calculation and the dialog were the same thing — a caller could not price a
 * line without showing a form. That is why a dealer, who has nothing to decide,
 * still had to click through a wizard.
 *
 * Extracted so there is ONE calculation with two presentations: the installer's
 * editable calculator and the dealer's automatic line. If this drifted from the
 * modal, the same boat would price differently depending on who was looking at
 * it, which is the worst possible failure for a number that becomes an invoice.
 *
 * Imports only the two zero-import pure modules, so this stays harness-testable
 * (`scripts/test/marineLinePricing.test.mjs`). Do not import anything that
 * reaches ScopeService or HTTPService.
 *
 * The `.js` extensions on those imports are REQUIRED, not stylistic: Node's ESM
 * resolver does not guess them, and webpack is happy either way. Drop them and
 * this module still builds but can no longer be tested.
 *
 * @param {object} service catalog row — rate, formula, factor, waste, duration
 * @param {object} vehicle the craft, nested `marine_attributes` or flat
 * @returns {{
 *   ok: boolean,            // fully determined; safe to add without asking
 *   area: number|null,      // billable square feet, whole
 *   rate: number|null,      // per square foot
 *   total: number|null,     // area x rate
 *   durationDays: number,   // whole-job, never scaled by area
 *   wastePct: number,
 *   variables: object,      // what the formula consumed, incl. estimate flag
 *   needsInput: string|null,// the one dimension a human must supply
 *   error: string|null,     // unresolvable formula, missing rate
 * }}
 */
export function priceMarineLine(service, vehicle) {
  const svc = service || {};
  const attrs = marineAttrsOf(vehicle);

  const blank = {
    ok: false, area: null, rate: null, total: null,
    durationDays: 1, wastePct: 0, variables: {}, needsInput: null, error: null,
  };

  // Catalog defaults, read exactly as the modal reads them. Duration is
  // whole-job: a bigger hull is more film, not proportionally more days.
  const wastePct = svc.waste_pct != null && Number.isFinite(Number(svc.waste_pct))
    ? Number(svc.waste_pct)
    : 0;
  const durationDays = Number(svc.duration_days) > 0 ? Number(svc.duration_days) : 1;

  let formula;
  try {
    formula = resolveAreaFormula(svc);
  } catch (err) {
    return { ...blank, wastePct, durationDays, error: err.message };
  }

  const rate = getMarineRate(svc);
  // Zero or absent is refused, not treated as free. A $0 area line looks like a
  // priced line and would invoice the work at nothing.
  if (!Number.isFinite(rate) || rate <= 0) {
    return {
      ...blank, wastePct, durationDays,
      error: 'This service has no $/sq ft rate, so the line cannot be priced.',
    };
  }

  // Fill each dimension the formula asks for. `needsInput` names the FIRST that
  // no data can supply, so the caller can prompt for exactly one thing rather
  // than showing an empty form.
  const variables = {};
  let needsInput = null;
  let estimatedFreeboard = false;

  for (const dim of formulaDimensions(formula)) {
    const known = attrs[dim];
    if (known !== null && known !== undefined && String(known) !== '') {
      variables[dim] = Number(known);
      continue;
    }
    // No feed carries freeboard, so most craft reach here. Derive it from
    // length rather than stopping — but record that it was derived, because
    // `learnedHullHeight` must never write an estimate back to the reference
    // row as though someone had measured it.
    if (dim === 'hull_height_in') {
      const est = estimateFreeboardIn(attrs.loa_ft);
      if (est !== null) {
        variables[dim] = est;
        estimatedFreeboard = true;
        continue;
      }
    }
    // `manual_area_sqft` always lands here by design — it means "someone
    // measured THIS job" and has no reference-data source.
    if (needsInput === null) needsInput = dim;
  }

  if (needsInput !== null) {
    return { ...blank, rate, wastePct, durationDays, variables, needsInput };
  }

  let area;
  try {
    area = computeMarineArea(svc, variables);
  } catch (err) {
    return { ...blank, rate, wastePct, durationDays, variables, error: err.message };
  }

  if (estimatedFreeboard) variables.hull_height_estimated = true;

  return {
    ok: true,
    area,
    rate,
    total: lineTotal(area, rate),
    durationDays,
    wastePct,
    variables,
    needsInput: null,
    error: null,
  };
}

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const cents = (v) => Math.round(v * 100) / 100;

/**
 * The two DEALER-FACING prices for an area line, as LINE TOTALS.
 *
 * `wholesale_price` and `retail_price` are frozen at the catalog PER-SQ-FT RATE
 * when a line is created, and the area calculator never updated them — so a
 * 220 sq ft line at $8.50/sq ft displayed and totalled as $8.50, beside a
 * CORRECT wholesale total. Half right reads as right, which is the worst shape
 * available for a number that becomes an invoice.
 *
 * `request_services.retail_price` is a LINE TOTAL server-side: stored verbatim,
 * never multiplied by quantity by any consumer, and read directly as
 * `retail_price - service_amount` to report dealer margin. Writing a rate there
 * reports margin as a large negative number, silently, in reporting rather than
 * in front of the estimator.
 *
 * Always derived from RATES, never from a previous price. That is what makes
 * re-confirming the calculator idempotent.
 */
export function computeAreaLinePrices({ wholesaleRate, retailRate, area }) {
  const sqft = num(area);
  const whole = num(wholesaleRate);
  // A service with no separate retail rate sells at the wholesale one. Zero
  // would read as "free" rather than "not set".
  //
  // null/undefined/'' are checked BEFORE Number(): Number(null) is 0 and passes
  // isFinite, so an absent rate would otherwise be indistinguishable from a
  // deliberate zero and the line would price at nothing. Same trap
  // extractAreaConfig documents for ''.
  const retailAbsent = retailRate === null || retailRate === undefined || retailRate === '';
  const retail = !retailAbsent && Number.isFinite(Number(retailRate)) ? Number(retailRate) : whole;
  return {
    wholesale_price: cents(whole * sqft),
    retail_price: cents(retail * sqft),
  };
}

/**
 * The dealer's retail total.
 *
 * NO `* quantity`. Every `retail_price` is already a line total — for an area
 * line because `computeAreaLinePrices` made it one, and for a unit line because
 * that is the column's contract server-side.
 */
export function sumRetail(lines) {
  const list = Array.isArray(lines) ? lines : [];
  return cents(list.reduce((sum, l) => sum + num(l?.retail_price ?? l?.service_cost), 0));
}
